import React, { useState, useEffect } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { PaperClipIcon, XMarkIcon, BanknotesIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import type { MatterDetailed } from '../../../../../types/matters';
import type { Expense, ExpenseCategory } from '../../../../billing/types/billing.types';
import { EXPENSE_CATEGORY_LABELS } from '../../../../billing/types/billing.types';

const CATEGORIES = Object.entries(EXPENSE_CATEGORY_LABELS) as [ExpenseCategory, string][];

interface ExpensesSectionProps {
  matter: MatterDetailed;
  expenses: Expense[];
  loading: boolean;
  createExpense: (dto: any) => Promise<Expense>;
}

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

const formatDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

const ExpenseSkeletonRow: React.FC = () => (
  <tr>
    <td className="px-3 py-2"><div className="lex-skeleton h-4 w-20" /></td>
    <td className="px-3 py-2"><div className="lex-skeleton h-4 w-40" /></td>
    <td className="px-3 py-2"><div className="lex-skeleton h-4 w-20" /></td>
    <td className="px-3 py-2"><div className="lex-skeleton h-4 w-20" /></td>
    <td className="px-3 py-2 text-right"><div className="lex-skeleton h-4 w-16 ml-auto" /></td>
    <td className="px-3 py-2 text-center"><div className="lex-skeleton h-4 w-4 mx-auto" /></td>
    <td className="px-3 py-2 text-center"><div className="lex-skeleton h-5 w-16 mx-auto rounded-full" /></td>
  </tr>
);

const ExpensesSection: React.FC<ExpensesSectionProps> = ({ matter, expenses, loading, createExpense }) => {
  const { keycloak } = useKeycloak();

  // Inline expense form state
  const [showExpenseForm, setShowExpenseForm] = useState(false);
  const [expDate, setExpDate] = useState(new Date().toISOString().split('T')[0]);
  const [expDescription, setExpDescription] = useState('');
  const [expCategory, setExpCategory] = useState<ExpenseCategory>('other');
  const [expVendor, setExpVendor] = useState('');
  const [expUnitCost, setExpUnitCost] = useState('');
  const [expQuantity, setExpQuantity] = useState('1');
  const [expNonBillable, setExpNonBillable] = useState(false);
  const [expSubmitting, setExpSubmitting] = useState(false);
  const [expError, setExpError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Receipt modal state
  const [receiptModal, setReceiptModal] = useState<{ expenseId: string; fileId: string } | null>(null);
  const [receiptImageUrl, setReceiptImageUrl] = useState<string | null>(null);
  const [receiptLoading, setReceiptLoading] = useState(false);

  useEffect(() => {
    if (!receiptModal) {
      if (receiptImageUrl) { URL.revokeObjectURL(receiptImageUrl); setReceiptImageUrl(null); }
      return;
    }
    let cancelled = false;
    setReceiptLoading(true);
    (async () => {
      try {
        const res = await fetch(
          `/api/v1/billing/expenses/${receiptModal.expenseId}/receipt/${encodeURIComponent(receiptModal.fileId)}`,
          { headers: { Authorization: `Bearer ${keycloak?.token || ''}` } }
        );
        if (!res.ok) throw new Error('Failed to load receipt');
        const blob = await res.blob();
        if (!cancelled) setReceiptImageUrl(URL.createObjectURL(blob));
      } catch {
        if (!cancelled) setReceiptImageUrl(null);
      } finally {
        if (!cancelled) setReceiptLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [receiptModal, keycloak?.token]);

  const handleSubmitExpense = async () => {
    if (!expDescription.trim()) { setExpError('Description is required.'); return; }
    if (!expUnitCost || Number(expUnitCost) <= 0) { setExpError('Unit cost is required.'); return; }
    setExpSubmitting(true);
    setExpError(null);
    try {
      await createExpense({
        expense_date: expDate,
        description: expDescription,
        expense_category: expCategory,
        vendor: expVendor || undefined,
        unit_cost: Number(expUnitCost),
        quantity: Number(expQuantity) || 1,
        is_non_billable: expNonBillable,
      });
      setShowExpenseForm(false);
      setExpDescription(''); setExpUnitCost(''); setExpVendor('');
      setExpQuantity('1'); setExpNonBillable(false);
      setExpCategory('other'); setExpDate(new Date().toISOString().split('T')[0]);
      setSuccessMsg('Expense saved');
      setTimeout(() => setSuccessMsg(null), 2000);
    } catch (err) {
      setExpError(err instanceof Error ? err.message : 'Failed to create expense');
    } finally {
      setExpSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-lg font-medium text-gray-900 dark:text-white">Expenses</h4>
        <button
          onClick={() => setShowExpenseForm(!showExpenseForm)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
        >
          <BanknotesIcon className="h-4 w-4" />
          Add Expense
        </button>
      </div>

      {/* Success toast */}
      {successMsg && (
        <div className="lex-success-toast flex items-center gap-2 px-4 py-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
          <CheckCircleIcon className="h-4 w-4 text-green-600 dark:text-green-400" />
          <span className="text-sm text-green-700 dark:text-green-300">{successMsg}</span>
        </div>
      )}

      {/* Inline Add Expense Form — animated expand */}
      {showExpenseForm && (
        <div className="lex-form-panel bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-lg p-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Date</label>
              <input type="date" value={expDate} onChange={(e) => setExpDate(e.target.value)}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Category</label>
              <select value={expCategory} onChange={(e) => setExpCategory(e.target.value as ExpenseCategory)}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white">
                {CATEGORIES.map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Vendor</label>
              <input type="text" value={expVendor} onChange={(e) => setExpVendor(e.target.value)} placeholder="Vendor name"
                className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Description *</label>
            <textarea value={expDescription} onChange={(e) => setExpDescription(e.target.value)} rows={2} placeholder="Describe the expense..."
              className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white resize-none" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Unit Cost *</label>
              <input type="number" step="0.01" min="0" value={expUnitCost} onChange={(e) => setExpUnitCost(e.target.value)} placeholder="0.00"
                className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Quantity</label>
              <input type="number" step="1" min="1" value={expQuantity} onChange={(e) => setExpQuantity(e.target.value)}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white" />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 py-1.5 cursor-pointer">
                <input type="checkbox" checked={expNonBillable} onChange={(e) => setExpNonBillable(e.target.checked)}
                  className="rounded border-gray-300 text-amber-600 focus:ring-amber-500" />
                <span className="text-sm text-gray-700 dark:text-gray-300">Non-billable</span>
              </label>
            </div>
          </div>
          {expError && <p className="text-xs text-red-500">{expError}</p>}
          <div className="flex justify-end gap-2">
            <button onClick={() => { setShowExpenseForm(false); setExpError(null); }}
              className="px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800">
              Cancel
            </button>
            <button onClick={handleSubmitExpense} disabled={expSubmitting}
              className="px-4 py-1.5 text-sm font-medium bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50">
              {expSubmitting ? 'Saving...' : 'Save Expense'}
            </button>
          </div>
        </div>
      )}

      {/* Expenses Table — skeleton loader + staggered row entrance */}
      {loading ? (
        <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300 font-medium">Date</th>
                <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300 font-medium">Description</th>
                <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300 font-medium">Category</th>
                <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300 font-medium">Vendor</th>
                <th className="px-3 py-2 text-right text-gray-600 dark:text-gray-300 font-medium">Amount</th>
                <th className="px-3 py-2 text-center text-gray-600 dark:text-gray-300 font-medium">Receipt</th>
                <th className="px-3 py-2 text-center text-gray-600 dark:text-gray-300 font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {[...Array(5)].map((_, i) => <ExpenseSkeletonRow key={i} />)}
            </tbody>
          </table>
        </div>
      ) : expenses.length === 0 ? (
        <div className="text-center py-6 text-gray-500 dark:text-gray-400 text-sm">
          No expenses for this engagement yet.
        </div>
      ) : (
        <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300 font-medium">Date</th>
                <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300 font-medium">Description</th>
                <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300 font-medium">Category</th>
                <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300 font-medium">Vendor</th>
                <th className="px-3 py-2 text-right text-gray-600 dark:text-gray-300 font-medium">Amount</th>
                <th className="px-3 py-2 text-center text-gray-600 dark:text-gray-300 font-medium">Receipt</th>
                <th className="px-3 py-2 text-center text-gray-600 dark:text-gray-300 font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {expenses.map((expense, i) => (
                <tr key={expense.expense_id} className="lex-list-row hover:bg-gray-50 dark:hover:bg-gray-800/50" style={{ '--row-index': i } as React.CSSProperties}>
                  <td className="px-3 py-2 text-gray-900 dark:text-white whitespace-nowrap">{formatDate(expense.expense_date)}</td>
                  <td className="px-3 py-2 text-gray-900 dark:text-white max-w-xs truncate">
                    <div className="flex items-center gap-2">
                      <span className="truncate">{expense.description}</span>
                      {expense.ai_categorized && (
                        <span className="flex-shrink-0 inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300 rounded">AI</span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-gray-700 dark:text-gray-300 whitespace-nowrap">
                    {EXPENSE_CATEGORY_LABELS[expense.expense_category] || expense.expense_category}
                  </td>
                  <td className="px-3 py-2 text-gray-700 dark:text-gray-300 whitespace-nowrap">{expense.vendor || '-'}</td>
                  <td className="px-3 py-2 text-right text-gray-900 dark:text-white font-medium tabular-nums whitespace-nowrap">
                    {formatCurrency(Number(expense.total_amount))}
                  </td>
                  <td className="px-3 py-2 text-center">
                    <button
                      onClick={() => {
                        if (expense.has_receipt && expense.receipt_attachments?.length) {
                          setReceiptModal({ expenseId: expense.expense_id, fileId: expense.receipt_attachments[0] });
                        }
                      }}
                      disabled={!expense.has_receipt}
                      className={`p-0.5 rounded ${expense.has_receipt ? 'hover:bg-green-50 dark:hover:bg-green-900/20 cursor-pointer' : 'cursor-default'}`}
                      title={expense.has_receipt ? 'View receipt' : 'No receipt'}
                    >
                      <PaperClipIcon className={`h-4 w-4 mx-auto ${expense.has_receipt ? 'text-green-500' : 'text-gray-300 dark:text-gray-600'}`} />
                    </button>
                  </td>
                  <td className="px-3 py-2 text-center">
                    {expense.is_billed ? (
                      <span className="inline-flex px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300">Billed</span>
                    ) : expense.is_non_billable ? (
                      <span className="inline-flex px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">Non-billable</span>
                    ) : (
                      <span className="inline-flex px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/20 dark:text-amber-300">Unbilled</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Receipt Image Modal */}
      {receiptModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70" onClick={() => setReceiptModal(null)}>
          <div className="relative max-w-2xl max-h-[90vh] bg-white dark:bg-gray-900 rounded-lg shadow-xl p-2" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setReceiptModal(null)}
              className="absolute top-2 right-2 z-10 p-1 bg-gray-100 dark:bg-gray-800 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700">
              <XMarkIcon className="h-5 w-5 text-gray-600 dark:text-gray-300" />
            </button>
            {receiptLoading && (
              <div className="flex items-center justify-center py-16 px-24">
                <div className="animate-spin h-8 w-8 border-2 border-blue-500 border-t-transparent rounded-full" />
              </div>
            )}
            {!receiptLoading && receiptImageUrl && (
              <img src={receiptImageUrl} alt="Receipt" className="max-w-full max-h-[85vh] object-contain rounded" />
            )}
            {!receiptLoading && !receiptImageUrl && (
              <p className="text-center text-gray-500 py-8 px-12">Failed to load receipt image.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpensesSection;
